// NotificationContext.js - 统一管理通知数据的Context

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';

import { useAuth } from './AuthContext';
import { notificationService } from '../services';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadedRef = useRef(false);

  // 🔥 核心：只在这里加载一次
  const loadNotifications = useCallback(async () => {
    if (!user?.id) {
      setNotifications([]);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.time('[NotificationProvider] Load Time');
      console.log('[NotificationProvider] 🔄 Loading notifications...');

      const result = await notificationService.getUserNotifications(user.id);

      if (result.success) {
        setNotifications(result.data || []);
        console.log(
          '[NotificationProvider] ✅ Loaded notifications:',
          result.data.length
        );
      } else {
        setError(result.error);
        console.error('[NotificationProvider] Failed to load:', result.error);
      }
    } catch (error) {
      console.error('[NotificationProvider] Error:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
      console.timeEnd('[NotificationProvider] Load Time');
    }
  }, [user?.id]);

  // 只在用户登录时加载一次
  useEffect(() => {
    // 🔧 【性能优化】防止 React Strict Mode 重复执行
    if (loadedRef.current || !user?.id) {
      if (loadedRef.current) {
        console.log('[NotificationProvider] Already loaded, skipping...');
      }
      return;
    }

    loadedRef.current = true;
    loadNotifications();
  }, [user?.id, loadNotifications]);

  // 监听通知变化事件（统一处理）
  useEffect(() => {
    if (!user?.id) return;

    const handleChange = () => {
      console.log(
        '[NotificationProvider] 🔔 Notification change detected, reloading...'
      );
      loadNotifications();
    };

    window.addEventListener('notif:unreadChanged', handleChange);
    window.addEventListener('follow:changed', handleChange);
    window.addEventListener('like:changed', handleChange);
    window.addEventListener('comment:added', handleChange);

    return () => {
      window.removeEventListener('notif:unreadChanged', handleChange);
      window.removeEventListener('follow:changed', handleChange);
      window.removeEventListener('like:changed', handleChange);
      window.removeEventListener('comment:added', handleChange);
    };
  }, [user?.id, loadNotifications]);

  // 计算未读数量
  const unreadCount = notifications.filter(n => !n.isRead).length;

  // 标记为已读
  const markAsRead = useCallback(async notificationId => {
    try {
      const result = await notificationService.markAsRead(notificationId);
      if (result.success) {
        setNotifications(prev =>
          prev.map(n => (n.id === notificationId ? { ...n, isRead: true } : n))
        );
      }
    } catch (error) {
      console.error('[NotificationProvider] Error marking as read:', error);
    }
  }, []);

  // 标记所有为已读
  const markAllAsRead = useCallback(async () => {
    if (!user?.id) return;

    try {
      const result = await notificationService.markAllAsRead(user.id);
      if (result.success) {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      }
    } catch (error) {
      console.error('[NotificationProvider] Error marking all as read:', error);
    }
  }, [user?.id]);

  const value = {
    notifications,
    unreadCount,
    isLoading,
    error,
    loadNotifications,
    markAsRead,
    markAllAsRead,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// 导出 hook
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotifications must be used within NotificationProvider'
    );
  }
  return context;
};
